import { useEffect, useRef, useState } from 'react'

import tokenFetcher from 'common/auth/tokenFetcher'
import { useAppSelector } from 'common/hooks/redux'

import { CustomerJourney } from '../BriefAnalysis'

export interface BriefAnalysisRequirement {
  requirement_id?: string // New API format
  requirement?: string // Old API format (for backward compatibility)
  fulfilled?: boolean // Old API format (for backward compatibility)
  score: number
  reasoning?: string // Old API format
  evidence?: string[] // New API format
}

export interface BriefAnalysis {
  requirements: BriefAnalysisRequirement[]
  overall_score?: number // Optional as new API may not include it
}

export interface BriefAnalysisResult {
  brief_analysis: BriefAnalysis
  topics: string[]
}

interface UseDebouncedBriefAnalysisParams {
  briefText: string
  customerJourneyPayload: CustomerJourney
  contentId?: string
  enabled?: boolean
}

export const useDebouncedBriefAnalysis = ({
  briefText,
  customerJourneyPayload,
  contentId,
  enabled = true,
}: UseDebouncedBriefAnalysisParams) => {
  const abortControllerRef = useRef<AbortController | null>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const previousCustomerJourneyRef = useRef<CustomerJourney | null>(null)
  const previousBriefTextRef = useRef<string>('')
  const [analysisResult, setAnalysisResult] =
    useState<BriefAnalysisResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const accountId = useAppSelector((state) => state.authStates.accountId)

  useEffect(() => {
    const makeAnalysisCall = async (abortController: AbortController) => {
      setIsLoading(true)
      try {
        const token: string = await tokenFetcher.getAccessToken()
        // TO DO : USE THE PROPER API
        const response = await fetch(
          `https://prompt-management.eu1.jacquard.com/${
            process.env.REACT_APP_ENVIRONMENT === 'production' ? 'prd' : 'qa'
          }/accounts/56f16412c3351a8442cbcdf0/campaigns/${contentId}/analyze-brief`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-account-id': accountId,
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              brief: briefText,
              customer_journey: {
                journey_title: customerJourneyPayload?.customerJourneyTitle,
                requirements: customerJourneyPayload?.requirements,
              },
            }),
            signal: abortController.signal,
          }
        )

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result: BriefAnalysisResult = await response.json()
        setAnalysisResult(result)
        setIsLoading(false)
      } catch (error: any) {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          setError(error)
        }
        setIsLoading(false)
      }
    }
    const customerJourneyChanged =
      previousCustomerJourneyRef.current &&
      previousCustomerJourneyRef.current.customerJourneyTitle !==
        customerJourneyPayload?.customerJourneyTitle

    const briefTextChanged = previousBriefTextRef.current !== briefText

    if (customerJourneyChanged) {
      setAnalysisResult(null)
    }

    previousCustomerJourneyRef.current = customerJourneyPayload
    previousBriefTextRef.current = briefText

    // Allow analysis when customer journey changes, even if enabled is false
    const shouldRunAnalysis = customerJourneyChanged || enabled

    if (
      !shouldRunAnalysis ||
      !contentId ||
      !briefText.trim() ||
      briefText.trim().length < 20
    ) {
      setIsLoading(false)
      return
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      setIsLoading(false)
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      setIsLoading(false)
    }

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setError(null)

    if (customerJourneyChanged && !briefTextChanged) {
      makeAnalysisCall(abortController)
    } else {
      debounceTimerRef.current = setTimeout(() => {
        makeAnalysisCall(abortController)
      }, 1500)
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [briefText, customerJourneyPayload, contentId, enabled, accountId])

  return { analysisResult, isLoading, error }
}
